import { useState, useEffect } from 'react';
import { FlowState, FlowStateConfig, NewFlowStateData } from '../types/flow.types';
import dataProvider from '../../services/api';

interface ErrorHandlerProps {
  showSuccessMessage: (message: string) => void;
  handleErrorWithLogout: (error: any) => void;
}

export const useFlowStates = (selectedFlow: number | null, { showSuccessMessage, handleErrorWithLogout }: ErrorHandlerProps) => {
  const [flowStates, setFlowStates] = useState<FlowState[]>([]);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [flowStateConfig, setFlowStateConfig] = useState<FlowStateConfig>({
    status: '',
    state: 0,
    nextState: -1,
    locationIndex: -1,
    showLocationTracker: true,
    canClientAdvance: true,
    canContractorAdvance: true,
    canClientCancel: true,
    canContractorCancel: true,
    iconUrl: ""
  });
  const [newFlowStateData, setNewFlowStateData] = useState<NewFlowStateData>({
    flowStateId: 0,
    flowId: 0,
    canClientCancel: true,
    canClientAdvance: true,
    canContractorCancel: true,
    canContractorAdvance: true,
    showTracker: true,
    locationIndex: -1,
    state: 0,
    nextState: -1,
    status: '',
  });
  const [openAddFlowStateModal, setOpenAddFlowStateModal] = useState(false);

  const fetchFlowStates = async () => {
    if (selectedFlow) {
      try {
        const response = await dataProvider.getFlowStatesByFlowId({
          flowId: selectedFlow,
        });
        const states = response.data;
        setFlowStates(states);
        if (states.length > 0) {
          setSelectedState(states[0].id);
          setFlowStateConfig({
            status: states[0].status,
            state: states[0].state,
            nextState: states[0].nextState,
            locationIndex: states[0].locationIndex,
            showLocationTracker: states[0].showTracker,
            canClientAdvance: states[0].canClientAdvance,
            canContractorAdvance: states[0].canContractorAdvance,
            canClientCancel: states[0].canClientCancel,
            canContractorCancel: states[0].canContractorCancel,
            iconUrl: states[0].iconUrl || ""
          });
        } else {
          setSelectedState(null);
          setFlowStateConfig({
            status: '',
            state: 0,
            nextState: -1, // Thay đổi từ 0 thành -1 để tránh lỗi
            locationIndex: -1,
            showLocationTracker: true,
            canClientAdvance: true,
            canContractorAdvance: true,
            canClientCancel: true,
            canContractorCancel: true,
            iconUrl: ""
          });
        }
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    } else {
      setFlowStates([]);
      setSelectedState(null);
      setFlowStateConfig({
        status: '',
        state: 0,
        nextState: -1, // Thay đổi từ 0 thành -1 để tránh lỗi
        locationIndex: -1,
        showLocationTracker: true,
        canClientAdvance: true,
        canContractorAdvance: true,
        canClientCancel: true,
        canContractorCancel: true,
        iconUrl: ""
      });
    }
  };
  
  // ... (phần còn lại của hook giữ nguyên)

  const handleUploadStateIcon = async (file: File) => {
    if (!selectedState) return;
  
    try {
      await dataProvider.uploadStateIcon({ flowStateId: selectedState, file });
      const response = await dataProvider.getFlowStatesByFlowId({ flowId: selectedFlow || 0 });
      setFlowStates(response.data);
      const updatedState = response.data.find((state: FlowState) => state.id === selectedState);
      if (updatedState) {
        setFlowStateConfig({
          ...flowStateConfig,
          iconUrl: updatedState.iconUrl || '',
        });
      }
      showSuccessMessage('State icon uploaded successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleDeleteFlowState = async () => {
    if (!selectedFlow || !selectedState) return;
    try {
      await dataProvider.deleteFlowState({
        flowId: selectedFlow,
        flowStateId: selectedState,
      });
      const response = await dataProvider.getFlowStatesByFlowId({ flowId: selectedFlow });
      setFlowStates(response.data);
      if (response.data.length > 0) {
        setSelectedState(response.data[0].id);
        setFlowStateConfig({
          status: response.data[0].status,
          state: response.data[0].state,
          nextState: response.data[0].nextState,
          locationIndex: response.data[0].locationIndex,
          showLocationTracker: response.data[0].showTracker,
          canClientAdvance: response.data[0].canClientAdvance,
          canContractorAdvance: response.data[0].canContractorAdvance,
          canClientCancel: response.data[0].canClientCancel,
          canContractorCancel: response.data[0].canContractorCancel,
          iconUrl: response.data[0].iconUrl || ""
        });
      } else {
        setSelectedState(null);
        setFlowStateConfig({
          status: '',
          state: 0,
          nextState: -1,
          locationIndex: -1,
          showLocationTracker: true,
          canClientAdvance: true,
          canContractorAdvance: true,
          canClientCancel: true,
          canContractorCancel: true,
          iconUrl: ""
        });
      }
      showSuccessMessage('Flow state deleted successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleUpdateFlowState = async () => {
    if (!selectedFlow || !selectedState) return;
    try {
      const payload = {
        flowId: selectedFlow,
        flowStateId: selectedState,
        status: flowStateConfig.status,
        state: flowStateConfig.state,
        nextState: flowStateConfig.nextState,
        locationIndex: flowStateConfig.locationIndex,
        showTracker: flowStateConfig.showLocationTracker,
        canClientAdvance: flowStateConfig.canClientAdvance,
        canContractorAdvance: flowStateConfig.canContractorAdvance,
        canClientCancel: flowStateConfig.canClientCancel,
        canContractorCancel: flowStateConfig.canContractorCancel,
        iconUrl: '',
      };
      await dataProvider.updateFlowState({ data: payload });
      const response = await dataProvider.getFlowStatesByFlowId({ flowId: selectedFlow });
      setFlowStates(response.data);
      showSuccessMessage('Flow state updated successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleAddFlowState = async () => {
    if (selectedFlow) {
      try {
        const payload = {
          ...newFlowStateData,
          flowId: selectedFlow,
          flowStateId: 0,
        };
        await dataProvider.createFlowState('FlowState/Create', { data: payload });
        setOpenAddFlowStateModal(false);
        setNewFlowStateData({
          flowStateId: 0,
          flowId: 0,
          canClientCancel: true,
          canClientAdvance: true,
          canContractorCancel: true,
          canContractorAdvance: true,
          showTracker: true,
          locationIndex: -1,
          state: 0,
          nextState: -1,
          status: '',
        });
        const response = await dataProvider.getFlowStatesByFlowId({ flowId: selectedFlow });
        setFlowStates(response.data);
        if (response.data.length > 0) {
          setSelectedState(response.data[response.data.length - 1].id);
        }
        showSuccessMessage('Flow state added successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  useEffect(() => {
    fetchFlowStates();
  }, [selectedFlow, dataProvider]);

  useEffect(() => {
    if (selectedState !== null && flowStates.length > 0) {
      const selectedStateData = flowStates.find(state => state.id === selectedState);
      if (selectedStateData) {
        setFlowStateConfig({
          status: selectedStateData.status,
          state: selectedStateData.state,
          nextState: selectedStateData.nextState,
          locationIndex: selectedStateData.locationIndex,
          showLocationTracker: selectedStateData.showTracker,
          canClientAdvance: selectedStateData.canClientAdvance,
          canContractorAdvance: selectedStateData.canContractorAdvance,
          canClientCancel: selectedStateData.canClientCancel,
          canContractorCancel: selectedStateData.canContractorCancel,
          iconUrl: selectedStateData.iconUrl || ""
        });
      }
    }
  }, [selectedState, flowStates]);

  return {
    flowStates,
    selectedState,
    setSelectedState,
    flowStateConfig,
    setFlowStateConfig,
    newFlowStateData,
    setNewFlowStateData,
    openAddFlowStateModal,
    setOpenAddFlowStateModal,
    handleUploadStateIcon,
    handleDeleteFlowState,
    handleUpdateFlowState,
    handleAddFlowState,
  };
};